import { useState, useEffect, useMemo } from "react";
import PageTemplate from '../components/PageTemplate';
import MainCarousel from '../components/MainCarousel';
import PopularDestinations from '../components/PopularDestinations';
import CardSection from '../components/CardSection';
import BannerSection from '../components/Banner';
import SearchResults from '../components/SearchResults';
import Loader from '../components/Loader';

import { groupByProperty, filterByTags } from "../../utils";
import { bookedListingIds } from "../../utils/bookedListings";
import { useApp } from '../../context';

export function IndexPage({ ipInfo }) {
  const [activeFilter] = useState('All');
  const [allProviders, setAllProviders] = useState([]);
  const [allCategoryFilters, setAllCategoryFilters] = useState({});

  const { serviceListing, bookings, serviceFilters } = useApp();

  useEffect(() => {
    if (!serviceListing.length) return;

    // `serviceFilters` is a dependency because it no longer resolves before
    // the listings listener starts — without it the category sections stayed
    // unrendered whenever the first snapshot won that race.
    setAllCategoryFilters(serviceFilters);

    // Shared with the listing page, which must derive `booked` for itself —
    // a listing opened directly by URL never passes through this merge.
    const bookingIds = bookedListingIds(bookings);

    // Copied, not mutated in place. These objects belong to the context's
    // snapshot array, and writing `booked` onto them meant a listing stayed
    // flagged as booked after the booking that flagged it had gone.
    const withBookingState = serviceListing.map((listing) => ({
      ...listing,
      booked: bookingIds.includes(listing.id),
    }));

    setAllProviders(withBookingState);
  }, [serviceListing, bookings, serviceFilters]);

  // Derived, so an incoming snapshot re-groups the listings without
  // discarding the filter the traveller has chosen. Storing this meant the
  // effect above overwrote it on every update, leaving the heading naming a
  // filter that was no longer applied to what was on screen.
  const providers = useMemo(() => {
    const source =
      activeFilter === 'All' ? allProviders : filterByTags(allProviders, [activeFilter]);
    return groupByProperty(source, "type");
  }, [allProviders, activeFilter]);

  // `restoreScroll` below: listings became a route, so pressing Back from one
  // REMOUNTS this page (AppRoutes keys on pathname) rather than dismissing a
  // modal over it. Without the opt-in restore, every traveller returning from
  // a listing landed back at the top of the feed.
  return (
    <PageTemplate restoreScroll scrollKey="home">
      <MainCarousel />
      {activeFilter === 'All'
        ? <>
          <PopularDestinations />
          <BannerSection />
          {
            allCategoryFilters && allCategoryFilters.subFilters && Object.keys(allCategoryFilters.subFilters).map((key, i) => {
              return <CardSection key={`CardSection-${key}-${i}`} title={key} filters={allCategoryFilters.subFilters[key]} serviceProviders={providers[key]} />
            })
          }
        </>
        : <SearchResults providers={providers} title={activeFilter} />}

      <Loader />
    </PageTemplate>
  );
}
